import { useState, useRef, useCallback } from "react";
import { Blurhash } from "react-blurhash";
import { Play, Pause, Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

export interface VideoPreviewProps {
  url: string;
  blurhash?: string | null;
  aspectRatio?: "9/16" | "16/9" | "1/1" | "4/3";
  showControls?: boolean;
  autoPlayOnHover?: boolean;
  className?: string;
}

export function VideoWithThumbnail({
  url,
  blurhash,
  aspectRatio = "9/16",
  showControls = true,
  autoPlayOnHover = true,
  className,
}: VideoPreviewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showBlurhash, setShowBlurhash] = useState(false);

  const handlePlay = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  }, []);

  const handlePause = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, []);

  const handleTogglePlay = useCallback(() => {
    if (isPlaying) {
      handlePause();
    } else {
      handlePlay();
    }
  }, [isPlaying, handlePlay, handlePause]);

  const handleMouseEnter = useCallback(() => {
    if (autoPlayOnHover && videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  }, [autoPlayOnHover]);

  const handleMouseLeave = useCallback(() => {
    if (autoPlayOnHover && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  }, [autoPlayOnHover]);

  const aspectRatioClass = {
    "9/16": "aspect-[9/16]",
    "16/9": "aspect-video",
    "1/1": "aspect-square",
    "4/3": "aspect-[4/3]",
  }[aspectRatio];

  const defaultBlurhash = "L6PZfSi_.AyE_3t7t7R**0o#DgR4";

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {/* Video container */}
      <div
        className={cn(
          "relative overflow-hidden rounded-xl bg-muted",
          aspectRatioClass,
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Blurhash placeholder - shows while loading or when toggled */}
        <div
          className={cn(
            "absolute inset-0 z-10 transition-opacity duration-500",
            isLoaded && !showBlurhash
              ? "opacity-0 pointer-events-none"
              : "opacity-100",
          )}
        >
          <Blurhash
            hash={blurhash || defaultBlurhash}
            width="100%"
            height="100%"
            resolutionX={32}
            resolutionY={32}
            punch={1}
            className="h-full w-full"
          />
        </div>

        {/* Video element */}
        <video
          ref={videoRef}
          src={url}
          className={cn(
            "h-full w-full object-cover transition-opacity duration-500",
            isLoaded ? "opacity-100" : "opacity-0",
          )}
          muted
          loop
          playsInline
          preload="metadata"
          onLoadedData={() => setIsLoaded(true)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        />

        {/* Loading indicator */}
        {!isLoaded && (
          <div className="absolute inset-0 z-20 flex items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          </div>
        )}
      </div>

      {/* Controls below video */}
      {showControls && (
        <div className="flex items-center justify-between gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-9 gap-2 bg-transparent"
            onMouseEnter={handleTogglePlay}
            onMouseLeave={handleTogglePlay}
            disabled={!isLoaded}
          >
            {isPlaying ? (
              <>
                <Pause className="h-4 w-4" />
                <span className="text-sm font-medium">Pause</span>
              </>
            ) : (
              <>
                <Play className="h-4 w-4" />
                <span className="text-sm font-medium">Play</span>
              </>
            )}
          </Button>

          <Button
            size="sm"
            variant={showBlurhash ? "default" : "outline"}
            className={cn("h-9 gap-2", !showBlurhash && "bg-transparent")}
            onMouseEnter={(e) => {
              e.preventDefault();
              setShowBlurhash(true);
            }}
            onMouseLeave={() => setShowBlurhash(false)}
          >
            {showBlurhash ? (
              <>
                <EyeOff className="h-4 w-4" />
                <span className="text-sm font-medium">Hide Blurhash</span>
              </>
            ) : (
              <>
                <Eye className="h-4 w-4" />
                <span className="text-sm font-medium">Show Blurhash</span>
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
